
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';
import NavLink from '../../components/NavLink';
import { EMAIL_CONFIG } from '../../config/email';

export default function Contact() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const sendConfirmationEmail = async (formData: any) => {
    try {
      // Use local server for development, Vercel API for production
      const apiEndpoint = window.location.hostname === 'localhost' 
        ? 'http://localhost:3001/api/send-email'
        : '/api/send-email';
        
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData,
          emailType: 'confirmation'
        })
      });

      if (response.ok) {
        console.log('Confirmation email sent successfully');
      } else {
        console.error('Failed to send confirmation email');
      }
    } catch (error) {
      console.error('Error sending confirmation email:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      // Use local server for development, Vercel API for production
      const apiEndpoint = window.location.hostname === 'localhost' 
        ? 'http://localhost:3001/api/send-email'
        : '/api/send-email';
        
      // Send email using appropriate API endpoint
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData,
          emailType: 'business'
        })
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Business email sent successfully:', result);
        
        // Send confirmation email to the customer
        await sendConfirmationEmail(formData);
        
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          interest: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        console.error('Email API Error:', errorData);
        console.error('Response status:', response.status);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Email sending error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white  transition-colors" style={{ fontFamily: 'Prompt, sans-serif' }}>
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start py-4">
            <div className="flex items-start">
              <Link to="/">
                <Logo size="md" className="hover:scale-105 transition-transform cursor-pointer" />
              </Link>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8 pt-4">
                <NavLink to="/">HOME</NavLink>
                <NavLink to="/about">ABOUT</NavLink>
                <NavLink to="/services">SERVICES</NavLink>
                <NavLink to="/products">PROJECTS</NavLink>
                <NavLink to="/contact">CONTACT</NavLink>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4 pt-4">
              <a href="/contact" className="bg-zbm-secondary hover:bg-zbm-secondary-dark text-white px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer drop-shadow-lg">
                Get a Quote
              </a>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-zbm-primary hover:text-zbm-primary-dark cursor-pointer drop-shadow-lg"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/95 /95 backdrop-blur-sm border-t border-gray-100 ">
              <NavLink to="/" mobile={true}>HOME</NavLink>
              <NavLink to="/about" mobile={true}>ABOUT</NavLink>
              <NavLink to="/services" mobile={true}>SERVICES</NavLink>
              <NavLink to="/products" mobile={true}>PROJECTS</NavLink>
              <NavLink to="/contact" mobile={true}>CONTACT</NavLink>
              <a href="/contact" className="bg-zbm-secondary hover:bg-zbm-secondary-dark text-white px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer w-full mt-2">
                Get a Quote
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Header Separator Line */}
      <div className="w-full h-px bg-gray-300 mt-32"></div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-20" style={{ backgroundColor: '#F7F7F7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-extrabold text-gray-900  mb-6" style={{ fontFamily: 'Prompt, sans-serif' }}>
            Get In
            <span className="text-zbm-primary block">Touch</span>
          </h1>
          <p className="text-xl text-gray-600  mb-8 max-w-3xl mx-auto leading-relaxed font-normal">
            Ready to start your construction project? Contact us today to discuss your infrastructure needs and receive a detailed quote.
          </p>
          <div className="flex items-center justify-center mb-8">
            <span className="text-2xl mr-3">📞</span>
            <p className="text-lg font-semibold text-gray-700 ">Let's Build Together</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900  mb-8" style={{ fontFamily: 'Prompt, sans-serif' }}>Contact Information</h2>
              
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-zbm-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900  mb-2" style={{ fontFamily: 'Prompt, sans-serif' }}>Address</h3>
                    <p className="text-gray-600  font-normal">📍 ZBM Projects</p>
                    <p className="text-gray-600  font-normal">Building B, Top Floor</p>
                    <p className="text-gray-600  font-normal">9 Armitage Road, Bird Sanctuary</p>
                    <p className="text-gray-600  font-normal">Pietermaritzburg 3201, South Africa</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-zbm-secondary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900  mb-2" style={{ fontFamily: 'Prompt, sans-serif' }}>Phone</h3>
                    <p className="text-gray-600  font-normal">📞 Cell: 068 140 3027</p>
                    <p className="text-gray-600  font-normal">☎️ Tel: 033 101 0773</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-zbm-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900  mb-2" style={{ fontFamily: 'Prompt, sans-serif' }}>Email</h3>
                    <p className="text-gray-600  font-normal">📧 zwanea@zbmprojects.co.za</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-zbm-secondary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-file-list-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900  mb-2" style={{ fontFamily: 'Prompt, sans-serif' }}>Company Registration</h3>
                    <p className="text-gray-600  font-normal">Reg: 2016/321746/07</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-zbm-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900  mb-2" style={{ fontFamily: 'Prompt, sans-serif' }}>Business Hours</h3>
                    <p className="text-gray-600  font-normal">Monday - Friday: 8:00 AM - 5:00 PM</p>
                    <p className="text-gray-600  font-normal">Saturday: By Appointment</p>
                    <p className="text-gray-600  font-normal">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="font-semibold text-gray-900  mb-6" style={{ fontFamily: 'Prompt, sans-serif' }}>Connect with us on social media to see our latest projects.</h3>
                <div className="flex space-x-4">
                  <a href="#" className="w-12 h-12 bg-zbm-primary hover:bg-zbm-primary-dark rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-zbm-secondary hover:bg-zbm-secondary-dark rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer">
                    <i className="ri-whatsapp-line text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-zbm-light to-zbm-light dark:from-gray-800 dark:to-gray-700 rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900  mb-8" style={{ fontFamily: 'Prompt, sans-serif' }}>Request a Quote</h2>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700  mb-2">First Name *</label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm bg-white  text-gray-900 "
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700  mb-2">Last Name *</label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm bg-white  text-gray-900 "
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700  mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm bg-white  text-gray-900 "
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700  mb-2">Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm bg-white  text-gray-900 "
                    placeholder="+27 XX XXX XXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700  mb-2">Project Type *</label>
                  <select 
                    name="interest"
                    value={formData.interest}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm pr-8 bg-white  text-gray-900 "
                  >
                    <option value="">Select project type</option>
                    <option value="Building Construction">Building Construction</option>
                    <option value="Civil Engineering">Civil Engineering</option>
                    <option value="Prefabricated Structures">Prefabricated Structures</option>
                    <option value="Water Infrastructure">Water Infrastructure</option>
                    <option value="Residential Development">Residential Development</option>
                    <option value="Renovation Project">Renovation Project</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700  mb-2">Message *</label>
                  <textarea 
                    rows={4}
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300  rounded-lg focus:ring-2 focus:ring-zbm-primary focus:border-transparent text-sm resize-none bg-white  text-gray-900 "
                    placeholder="Describe your construction project requirements..."
                  ></textarea>
                  <p className="text-xs text-gray-500  mt-1 font-normal">Maximum 500 characters ({formData.message.length}/500)</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-600 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">
                    Thank you for your inquiry! Your message has been sent successfully. Our team will review your project requirements and get back to you within 24-48 hours with a detailed quote.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                    Sorry, there was an error sending your message. Please check your message length and try again.
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-zbm-primary to-zbm-secondary hover:from-zbm-primary-dark hover:to-zbm-secondary-dark disabled:from-gray-400 disabled:to-gray-500 text-white py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Quote Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Prompt, sans-serif' }}>
                ZBM Projects
              </h3>
              <p className="text-gray-300 mb-6 max-w-md font-normal">
                A 100% Black-owned private company specializing in prefabricated structures, civil and building construction projects. Delivering high-quality infrastructure solutions across South Africa.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-zbm-primary hover:bg-zbm-primary-dark rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-zbm-secondary hover:bg-zbm-secondary-dark rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Prompt, sans-serif' }}>Services</h4>
              <ul className="space-y-2 text-gray-300 font-normal">
                <li><Link to="/services" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Project Management</Link></li>
                <li><Link to="/services" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Building Construction</Link></li>
                <li><Link to="/services" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Civil Engineering</Link></li>
                <li><Link to="/services" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Prefabricated Structures</Link></li>
                <li><Link to="/services" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Water Infrastructure</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Prompt, sans-serif' }}>Company</h4>
              <ul className="space-y-2 text-gray-300 font-normal">
                <li><Link to="/" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="hover:text-zbm-primary-light transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/about#team" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Our Team</Link></li>
                <li><Link to="/products" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Projects</Link></li>
                <li><Link to="/contact" className="hover:text-zbm-primary-light transition-colors cursor-pointer">Contact Us</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm font-normal">
              © 2025 ZBM Projects. All Rights Reserved. Reg: 2016/321746/07
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
